import React from 'react';
import type { CounterDepositsMethods, RegisterReport } from '../../types/registerReport';
import { normalizeReportSections } from '../../utils/registerReportSections';
import {
  CHANNEL_LABELS, PLATFORM_LABELS, PRINT_SECTION_LABELS, TYPE_LABELS, euro, signedEuro, totalEuro,
} from './registerFormat';

/**
 * «Σύνοψη» του «Ταμείο»: το κλασικό Ζ/Χ όπως το έδειχνε το παλιό παράθυρο — ίδιες ενότητες, ετικέτες και
 * ποσά — σε κάρτες δίπλα-δίπλα αντί για μία στενή στήλη. Οι «Εισπράξεις χρηστών» είναι στην καρτέλα
 * «Προσωπικό» και τα «Κεράσματα & ακυρώσεις» στη δική τους· οι ενότητες εκτύπωσης του καταστήματος
 * ορίζουν μόνο το χαρτί, όχι την οθόνη.
 *
 * Το πλέγμα μετρά στήλες από το πλάτος του παραθύρου, όχι της οθόνης (1 στο κινητό, 4 στα 1200px): σε
 * ταμεία 1024×768 η εφαρμογή τρέχει με CSS zoom (public/index.html), οπότε τα breakpoints της οθόνης
 * (md/xl) θα έδιναν λιγότερες στήλες από όσες χωρούν.
 */

type Waste = { entries: number; qty: number; value: number };
type ReportWithWaste = RegisterReport & { waste?: Waste | null };

interface Props {
  report: RegisterReport;
  /** Ρύθμιση του καταστήματος από τη σύνδεση — όταν η αναφορά δεν φέρνει `sections` (παλιό backend). */
  storeSections?: string[] | null;
  /** Ο χρήστης κλείνει ταμείο: υπενθύμιση φύρας όταν δεν έχει καταχωρηθεί καμία. */
  closeAllowed?: boolean;
}

const Row: React.FC<{ label: string; value: string; bold?: boolean; sub?: boolean }> = ({ label, value, bold, sub }) => (
  <div className={`flex items-center justify-between gap-3 ${sub ? 'pl-3' : ''} ${bold ? 'text-t1 font-bold' : 'text-t2'} text-sm py-0.5`}>
    <span className={sub ? 'text-t3' : ''}>{label}</span>
    <span className="tabular-nums shrink-0">{value}</span>
  </div>
);

const Card: React.FC<{ title?: string; testId?: string; children: React.ReactNode }> = ({ title, testId, children }) => (
  <div data-testid={testId} className="bg-surface-2 dark:bg-surface-2/40 rounded-xl border border-line-strong/50 p-4">
    {title && <p className="text-xs font-semibold text-t3 uppercase tracking-wider mb-2">{title}</p>}
    {children}
  </div>
);

const Total: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div className="border-t border-line-strong/50 mt-1 pt-1">
    <Row label={label} value={euro(value)} bold />
  </div>
);

/** Μετρητά/Κάρτα/IRIS/Προπληρ./Επί πιστώσει μιας γραμμής καναλιού ή πλατφόρμας — μόνο όσα ≠ 0. */
const methodRows = (r: { cash: number; card: number; iris: number; prepaid: number; credit?: number }) =>
  ([
    ['Μετρητά', r.cash],
    ['Κάρτα', r.card],
    ['IRIS', r.iris],
    ['Προπληρ.', r.prepaid],
    ['Επί πιστώσει', r.credit ?? 0],
  ] as [string, number][]).filter(([, v]) => Math.abs(v) > 0.001);

/** Μετρητά/Κάρτα/IRIS μιας γραμμής προκαταβολών ταμείου — μόνο όσα ≠ 0. */
const splitRows = (r: Partial<CounterDepositsMethods> | null | undefined) =>
  ([
    ['Μετρητά', r?.cash ?? 0],
    ['Κάρτα', r?.card ?? 0],
    ['IRIS', r?.iris ?? 0],
  ] as [string, number][]).filter(([, v]) => Math.abs(Number(v) || 0) > 0.001);

const RegisterSummary: React.FC<Props> = ({ report: baseReport, storeSections, closeAllowed }) => {
  const report = baseReport as ReportWithWaste;

  const payRows = ([
    ['Μετρητά', report.cashTotal],
    ['Κάρτα', report.cardTotal],
    ['IRIS', report.irisTotal],
    ['Προπληρωμένες', report.prepaidTotal],
    // «Επί πιστώσει» από take away και τραπέζια (εξοφλείται αργότερα) — δική του γραμμή, ούτε
    // μετρητά, ούτε κάρτα, ούτε «Λοιπά».
    ['Επί πιστώσει', report.creditTotal ?? 0],
    // Προκαταβολές κρατήσεων (Sala): πληρώθηκαν online στη Viva του μαγαζιού — ούτε συρτάρι, ούτε κάρτα.
    ['Προκαταβολές online', report.depositTotal ?? 0],
    // Προκαταβολές ταμείου που συμψηφίστηκαν σε λογαριασμό ή κρατήθηκαν: κανένα νέο χρήμα στην περίοδο
    // (εισπράχθηκαν όταν δόθηκαν) — δική τους γραμμή, ποτέ στα μετρητά/κάρτα.
    ['Προκαταβολές ταμείου (συμψηφισμός)', report.counterSettledTotal ?? 0],
    // Ό,τι είναι στο σύνολο εισπράξεων αλλά σε κανέναν από τους παραπάνω τρόπους (πληρωμές
    // τραπεζιών «λοιπά») — ώστε οι γραμμές να αθροίζουν.
    ['Λοιπά', report.otherTotal ?? 0],
  ] as [string, number][]).filter(([, v]) => Math.abs(v) > 0.001);

  // Παραστατικά − εισπράξεις: εμφανίζεται μόνο όταν διαφέρουν.
  const fiscalDiff = Number(report.fiscalCollectionsDiff) || 0;
  const hasFiscalDiff = Math.abs(fiscalDiff) > 0.005;
  // Πιστωτικά για παραστατικά προηγούμενων ημερών (29/10/2026): στα φορολογικά σύνολα, όχι στη σύγκριση —
  // δική τους γραμμή, με το αρχικό. Λείπει από παλιό backend/snapshot.
  const prior = report.priorPeriodCredits && report.priorPeriodCredits.count > 0 ? report.priorPeriodCredits : null;
  const day = (iso: string) => new Date(iso).toLocaleDateString('el-GR', { timeZone: 'Europe/Athens', day: '2-digit', month: '2-digit' });

  // Λογαριασμοί τραπεζιών που έκλεισαν στην περίοδο — χωριστά από τις «παραγγελίες» (που μετρούν
  // μόνο γραμμές παραγγελιών, όχι τραπέζια).
  const tableBills = report.dineInClosedSessions;
  const showTableBills = tableBills != null && (tableBills > 0 || !!report.dineIn);

  const sections = normalizeReportSections(report.sections ?? storeSections);

  const cashMoves = report.cashMovements && report.cashMovements.totals ? report.cashMovements : null;

  // Προκαταβολές ταμείου: πληροφοριακά. Τα χρήματα είναι ΗΔΗ στα Μετρητά/Κάρτα/IRIS παραπάνω.
  const counter = report.counterDeposits;
  const showCounter = !!counter && [counter.received?.total, counter.refunded?.total, counter.settled]
    .some(v => Math.abs(Number(v) || 0) > 0.001);

  return (
    <div className="space-y-4">
      <div className="grid gap-3 items-start grid-cols-[repeat(auto-fill,minmax(min(100%,17rem),1fr))]">
        <Card title="Σύνολα (φορολογικά)">
          <Row label="Καθαρό" value={euro(report.netTotal)} />
          <Row label="ΦΠΑ" value={euro(report.vatTotal)} />
          <Total label="Σύνολο" value={report.fiscalGross} />
        </Card>

        {(payRows.length > 0 || hasFiscalDiff || prior) && (
          <Card title="Εισπράξεις ανά τρόπο" testId="register-payments">
            {payRows.map(([label, v]) => <Row key={label} label={label} value={euro(v)} />)}
            <Total label="Σύνολο εισπράξεων" value={report.grossTotal} />
            {prior && (
              <div data-testid="register-prior-credits">
                <Row label="Πιστωτικά προηγούμενων ημερών" value={signedEuro(prior.gross)} />
                {prior.items.map(i => (
                  <Row
                    key={`${i.code}-${i.series}/${i.number}`}
                    sub
                    label={`${TYPE_LABELS[i.code] ?? i.code} ${i.series}/${i.number} για ${TYPE_LABELS[i.original.code] ?? i.original.code} ${i.original.series}/${i.original.number} (${day(i.original.issuedAt)})`}
                    value={signedEuro(i.gross)}
                  />
                ))}
                <p className="text-[11px] text-t4 mt-0.5">
                  Μετρούν στα φορολογικά σύνολα· τα χρήματα του αρχικού μετρήθηκαν στο Ζ της ημέρας του.
                </p>
              </div>
            )}
            {hasFiscalDiff && (
              <div data-testid="register-fiscal-diff">
                <Row label="Παραστατικά − εισπράξεις" value={signedEuro(fiscalDiff)} />
                <p className="text-[11px] text-t4 mt-0.5">
                  {fiscalDiff > 0
                    ? 'Εκδόθηκαν παραστατικά για περισσότερα από όσα εισπράχθηκαν.'
                    : 'Εισπράχθηκαν περισσότερα από όσα έχουν παραστατικό στην περίοδο.'}
                </p>
              </div>
            )}
          </Card>
        )}

        {/* Κινήσεις ταμείου (migration 277): μόνο σε κατάστημα με τη λειτουργία ή με κινήσεις στην περίοδο.
            Ίδιες γραμμές με το χαρτί του Ζ — «Μετρητά στο συρτάρι» = μετρητά + καταθέσεις − έξοδα − αναλήψεις. */}
        {cashMoves && (
          <Card title="Κινήσεις ταμείου" testId="register-cash-movements">
            <Row label="Μετρητά εισπράξεων" value={euro(report.cashTotal)} />
            {cashMoves.totals.counts.deposit > 0 && (
              <Row label={`Καταθέσεις (${cashMoves.totals.counts.deposit})`} value={signedEuro(cashMoves.totals.deposit)} />
            )}
            {cashMoves.totals.counts.expense > 0 && (
              <Row label={`Έξοδα (${cashMoves.totals.counts.expense})`} value={signedEuro(-cashMoves.totals.expense)} />
            )}
            {cashMoves.totals.counts.withdrawal > 0 && (
              <Row label={`Αναλήψεις (${cashMoves.totals.counts.withdrawal})`} value={signedEuro(-cashMoves.totals.withdrawal)} />
            )}
            {cashMoves.totals.voided.count > 0 && (
              <Row label={`Ακυρωμένες (${cashMoves.totals.voided.count})`} value={euro(cashMoves.totals.voided.value)} sub />
            )}
            <div className="border-t border-line-strong/50 mt-1 pt-1" data-testid="register-drawer-cash">
              <Row label="Μετρητά στο συρτάρι" value={totalEuro(cashMoves.drawerCash)} bold />
            </div>
          </Card>
        )}

        {/* Τραπέζια: οι εισπράξεις τους είναι ήδη στα παραπάνω σύνολα· εδώ η ανάλυσή τους. Ο αριθμός
            είναι τα τραπέζια με τουλάχιστον μία πληρωμή, όχι όσα έκλεισαν. */}
        {report.dineIn && (
          <Card title={`Τραπέζια με είσπραξη (${report.dineIn.sessions})`}>
            {([
              ['Μετρητά', report.dineIn.cash],
              ['Κάρτα', report.dineIn.card],
              ['IRIS', report.dineIn.iris],
              ['Επί πιστώσει', report.dineIn.credit ?? 0],
              ['Προκαταβολές', report.dineIn.deposit ?? 0],
              ['Λοιπά', report.dineIn.other],
            ] as [string, number][]).filter(([, v]) => Math.abs(v) > 0.001)
              .map(([label, v]) => <Row key={label} label={label} value={euro(v)} />)}
            <Total label="Σύνολο τραπεζιών" value={report.dineIn.total} />
          </Card>
        )}

        {showCounter && counter && (
          <Card title="Προκαταβολές ταμείου" testId="register-counter-deposits">
            <Row label="Εισπράχθηκαν" value={euro(counter.received?.total ?? 0)} bold />
            {splitRows(counter.received).map(([l, v]) => <Row key={`in-${l}`} label={l} value={euro(v)} sub />)}
            <Row label="Επιστράφηκαν" value={euro(counter.refunded?.total ?? 0)} bold />
            {splitRows(counter.refunded).map(([l, v]) => <Row key={`out-${l}`} label={l} value={euro(v)} sub />)}
            <p className="text-[11px] text-t4 mt-1">Ήδη μέσα στα Μετρητά/Κάρτα/IRIS — δεν προστίθενται ξανά</p>
          </Card>
        )}

        {report.byChannel && report.byChannel.length > 0 && (
          <Card title="Ανά κανάλι">
            {report.byChannel.map(c => (
              <div key={c.channel} className="mb-2 last:mb-0">
                <Row label={`${CHANNEL_LABELS[c.channel] || c.channel} (${c.count})`} value={euro(c.gross)} bold />
                {methodRows(c).map(([l, v]) => <Row key={l} label={l} value={euro(v)} sub />)}
              </div>
            ))}
          </Card>
        )}

        {report.byPlatform && report.byPlatform.length > 0 && (
          <Card title="Ανά πλατφόρμα">
            {report.byPlatform.map(p => (
              <div key={p.platform} className="mb-2 last:mb-0">
                <Row label={`${PLATFORM_LABELS[p.platform] || p.platform} (${p.count})`} value={euro(p.gross)} bold />
                {methodRows(p).map(([l, v]) => <Row key={l} label={l} value={euro(v)} sub />)}
              </div>
            ))}
          </Card>
        )}

        {report.documentTypes.length > 0 && (
          <Card title="Ανά παραστατικό">
            {report.documentTypes.map(t => (
              <div key={t.code} className="mb-2 last:mb-0">
                <Row label={`${TYPE_LABELS[t.code] || t.code} (${t.count})`} value={euro(t.gross)} bold />
                <Row label="Καθαρό" value={euro(t.net)} sub />
                <Row label="ΦΠΑ" value={euro(t.vat)} sub />
              </div>
            ))}
          </Card>
        )}

        {report.byVat && report.byVat.length > 0 && (
          <Card title="Ανάλυση ΦΠΑ">
            {report.byVat.map(v => (
              <div key={v.rate ?? 'na'} className="mb-2 last:mb-0">
                <Row label={v.rate != null ? `ΦΠΑ ${v.rate}%` : 'ΦΠΑ –'} value={euro(v.gross)} bold />
                <Row label="Καθαρό" value={euro(v.net)} sub />
                <Row label="ΦΠΑ" value={euro(v.vat)} sub />
              </div>
            ))}
          </Card>
        )}

        {/* Τέλη & εισφορές — αναλυτικά ανά κατηγορία (σακούλες / εισφορά). */}
        {report.fees && report.fees.length > 0 ? (
          <Card title="Τέλη & Εισφορές">
            {report.fees.map(f => (
              <Row key={f.category ?? f.label} label={`${f.label} (${f.units} τεμ)`} value={euro(f.amount)} />
            ))}
            {report.fees.length > 1 && <Total label="Σύνολο τελών" value={report.plasticFees} />}
          </Card>
        ) : Math.abs(report.plasticFees) > 0.001 ? (
          <Card>
            <Row label="Τέλη πλαστικών" value={euro(report.plasticFees)} bold />
          </Card>
        ) : null}

        {/* Φύρα περιόδου — μόνο για καταστήματα με ενεργή την καταγραφή. */}
        {report.waste && (
          report.waste.entries > 0 ? (
            <Card>
              <Row label={`Φύρα (${report.waste.qty} τεμ)`} value={euro(report.waste.value)} bold />
              <p className="text-[11px] text-t4 mt-1">Αξία σε τιμές πώλησης — δεν επηρεάζει τα σύνολα</p>
            </Card>
          ) : closeAllowed ? (
            <div data-testid="register-waste-hint" className="bg-amber-500/10 rounded-xl border border-amber-500/30 p-3 text-xs text-amber-800 dark:text-amber-300">
              Δεν έχει καταχωρηθεί φύρα για την περίοδο — αν υπάρχει, καταχώρησέ τη από το μενού («Φύρα») πριν το κλείσιμο.
            </div>
          ) : null
        )}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 text-xs text-t3 dark:text-t4">
        <p data-testid="register-counts">
          {report.orderCount} παραγγελίες στην περίοδο
          {showTableBills && ` · ${tableBills} ${tableBills === 1 ? 'λογαριασμός τραπεζιού' : 'λογαριασμοί τραπεζιών'}`}
        </p>
        <p data-testid="register-print-sections">
          Στο χαρτί τυπώνονται: {sections.map(s => PRINT_SECTION_LABELS[s]).join(' · ')}
        </p>
      </div>
    </div>
  );
};

export default RegisterSummary;
